import React from "react";
import { graphql, StaticQuery } from "gatsby";

import Helmet from "react-helmet";
import GlobalLayout from "components/page/GlobalLayout";
import CameraDetail from "components/cameras/detail/CameraDetailPage";
import {
  CTA_BUTTON_COPY_6,
  LIVE_DEMO_LINK,
} from "components/data/landing-page-data";

export default function R360() {
  const renderContent = (data) => {
    const camera = {
      model: "R360",
      type: "Fisheye",
      description:
        "Immersive 360˚ fisheye with multiple viewing options for maximum coverage.",
      webHeader: data.webImage,
      mobileHeader: data.mobileImage,
      objectPosition: "80% bottom",
      dims: data.dims,
      pdf: "/files/specs/R360.pdf",
      specs: {
        column1: [
          {
            title: "Resolution",
            text: "12MP Progressive CMOS sensor",
          },
          {
            title: "Aperture",
            text: "F/2.8",
          },
          {
            title: "Pan / Tilt / Zoom",
            text: "Up to 2X digital PTZ",
          },
          {
            title: "Field of View",
            text: [
              "180° Diagonal FoV",
              "180° Horizontal FoV",
              "180° Vertical FoV",
            ],
          },
          {
            title: "Focal Length",
            text: "Immersive view — live, real-time dewarping",
          },
          {
            title: "Dimensions",
            text: "130mm (5.11in) x 58mm (3.54in)",
          },
          {
            title: "Weight",
            text: "0.57kg (1.25lbs)",
          },
          {
            title: "Weather Resistance",
            text: "IP66",
          },
          {
            title: "Impact Resistance",
            text: "IK10",
          },
          {
            title: "Night Vision",
            text: "Infrared illuminators (16ft in low light)",
          },
        ],
        column2: [
          {
            title: "Onboard Storage Included",
            text: ["512GB or 30 days", "1TB or 60 days"],
          },
          {
            title: "Ethernet",
            text: "10 / 100Mbps, MDI / MDI-X Auto Sensing",
          },
          {
            title: "Power-Over-Ethernet",
            text: "Standard 802.3af for one-cable installation",
          },
          {
            title: "Power Consumption",
            text: "Max 9.9 watts",
          },
          {
            title: "Operating Temperature",
            text: ["-30˚ to 55˚ C", "-22˚ to 131˚ F"],
          },
          {
            title: "Operating Humidity",
            text: "10% - 90% RH",
          },
          {
            title: "In the Box",
            text: "Torx key, mounting screws, anchors, waterproof ethernet connector",
          },
          {
            title: "Baseline Steady State",
            text: "10 - 30 Kbps per camera",
          },
          {
            title: "HD WAN Live Streaming",
            text: "530 - 720 Kbps per camera",
          },
          {
            title: "CE, FCC, IC, UK, AUS, NZ, NDAA, TAA Compliant, UL Listed",
          },
          {
            title: "10-Year Warranty Included",
          },
        ],
      },
      accessoryCompatible: true,
      sensorCompatible: false,
      accessories: [
        "ACCR2CB",
        "ACCR2ARM",
        "ACCR2CM",
        "ACCR3R4CAP",
        "ACCR2PB",
        "ACCARMJB",
        "ACCR23JB",
        "ACCROD40CM",
        "ACCDCM1",
      ],
      exploreModels: {
        title: "Explore Our Other Cameras",
        subtitle:
          "Find the solution that’s best suited for your needs. Compare all camera models or view other models below.",
        cameraCards: [
          {
            image: data.domeCamera,
            mobileImage: data.domeCameraMobile,
            alt: "Dome camera model",
            width: "189px",
            model: "Dome Cameras",
            useCase: "Ideal for most environments.",
            link: "/cameras/dome-cameras/",
          },
          {
            image: data.bulletCamera,
            mobileImage: data.bulletCameraMobile,
            alt: "Bullet camera model",
            width: "189px",
            model: "Bullet Cameras",
            useCase: "Ideal for outdoor environments.",
            link: "/cameras/bullet-cameras/",
          },
        ],
      },
    };
    return (
      <GlobalLayout color="transparent" dark>
        <Helmet>
          <title>Rhombus R360 Fisheye Security Camera</title>
          <meta
            name="description"
            content="12MP panoramic video surveillance camera with modern cloud management, edge-based analytics, and durable design."
          />
          <meta
            name="thumbnail"
            content="https://rhombus.com/img/r360-seo.png"
          />
        </Helmet>
        <CameraDetail
          camera={camera}
          ctaCopy={CTA_BUTTON_COPY_6}
          ctaLink={LIVE_DEMO_LINK}
        />
      </GlobalLayout>
    );
  };
  const GET_IMAGES = graphql`
    query {
      webImage: file(
        relativePath: {
          eq: "components/cameras/detail/img/r360-header-2000-min.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 100, placeholder: BLURRED)
        }
      }
      mobileImage: file(
        relativePath: {
          eq: "components/cameras/detail/img/r360-header-mobile-500-min.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 100, placeholder: BLURRED)
        }
      }
      dims: file(
        relativePath: {
          eq: "components/cameras/detail/img/r360-dims-500-min.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 100, placeholder: BLURRED)
        }
      }
      domeCamera: file(
        relativePath: { eq: "components/cameras/img/explore-r120-200-min.png" }
      ) {
        childImageSharp {
          gatsbyImageData(placeholder: BLURRED)
        }
      }
      domeCameraMobile: file(
        relativePath: {
          eq: "components/cameras/img/explore-r120-200-mobile-min.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(placeholder: BLURRED)
        }
      }
      bulletCamera: file(
        relativePath: { eq: "components/cameras/img/explore-r500-510-min.png" }
      ) {
        childImageSharp {
          gatsbyImageData(placeholder: BLURRED)
        }
      }
      bulletCameraMobile: file(
        relativePath: {
          eq: "components/cameras/img/explore-r500-510-mobile-min.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(placeholder: BLURRED)
        }
      }
    }
  `;
  return <StaticQuery query={GET_IMAGES} render={renderContent} />;
}
